/**
 * UI-facing shapes of tool results. The backend modules are built in parallel, so every field is
 * optional-tolerant and pages normalise via `pickArray`/`pickObject`. The shapes the UI expects are
 * documented in docs/modules/web.md ("Tool result shapes the UI reads").
 */
import type { BrandConfig } from "@mimic/core/domain";

export type { BrandConfig };

export type Platform = "meta" | "tiktok" | "apple_search_ads" | "google_ads";
export const PLATFORMS: Platform[] = ["meta", "google_ads", "tiktok", "apple_search_ads"];

export const EMOTIONAL_TRIGGERS = [
  "social_proof",
  "urgency",
  "curiosity",
  "transformation",
  "belonging",
  "contrarian",
  "fear_of_missing_out",
  "relief",
  "aspiration",
  "humor",
] as const;
export type EmotionalTrigger = (typeof EMOTIONAL_TRIGGERS)[number];

export const AWARENESS_STAGES = ["unaware", "problem_aware", "solution_aware", "product_aware", "most_aware"] as const;
export type AwarenessStage = (typeof AWARENESS_STAGES)[number];

export const CREATIVE_FORMATS = [
  "static",
  "ugc_video",
  "demo_video",
  "testimonial_video",
  "carousel",
  "meme",
  "before_after",
  "listicle",
  "us_vs_them",
  "founder_story",
  "screen_recording",
] as const;

export const REJECTION_REASONS = [
  "font",
  "captions",
  "looks_ai",
  "text_garbled",
  "off_brand",
  "wrong_claim",
  "weak_hook",
  "bad_composition",
  "wrong_aspect",
  "policy_risk",
  "duplicate",
  "other",
] as const;
export type RejectionReason = (typeof REJECTION_REASONS)[number];

export const REJECTION_LABEL: Record<RejectionReason, string> = {
  font: "Font",
  captions: "Captions",
  looks_ai: "Looks AI",
  text_garbled: "Text garbled",
  off_brand: "Off-brand",
  wrong_claim: "Wrong claim",
  weak_hook: "Weak hook",
  bad_composition: "Composition",
  wrong_aspect: "Wrong aspect",
  policy_risk: "Policy risk",
  duplicate: "Duplicate",
  other: "Other",
};

export type CreativeStatus =
  | "draft"
  | "generating"
  | "qa_failed"
  | "pending_approval"
  | "approved"
  | "rejected"
  | "published"
  | "live"
  | "killed"
  | "graveyard"
  | "dead";

export type Verdict = "insufficient_data" | "watch" | "keep" | "scale" | "iterate" | "kill" | "graveyard" | "revive" | "retire";

export type OrgRole = "owner" | "admin" | "member";
export type BrandRole = "manager" | "editor" | "viewer";

// ─── Auth ──────────────────────────────────────────────────────────────────

/** GET /api/me → { user, actor, via, orgRole, brands:[{id,name,slug,status,logoUrl,role}], permissions:{org[], brands:{[id]: perm[]}}, agentName } */
export interface Me {
  id: string;
  email: string;
  name?: string;
  orgRole: OrgRole;
  brandRoles?: Record<string, BrandRole> | { brandId: string; role: BrandRole }[];
  permissions?: { org: string[]; brands: Record<string, string[]> };
  agentName?: string;
  via?: string;
}

// ─── Brands ────────────────────────────────────────────────────────────────

export interface OnboardingState {
  steps?: Record<string, { status: "pending" | "running" | "complete" | "skipped" | "not_run" | "failed"; detail?: string }>;
  step: string;
  progress: number;
  log: { at: string; msg: string }[];
  error?: string;
}

export interface Brand {
  id: string;
  slug?: string;
  name: string;
  websiteUrl?: string | null;
  context?: string;
  logoUrl?: string | null;
  status?: "onboarding" | "review" | "active" | "paused" | "archived";
  config?: BrandConfig;
  configVersion?: number;
  timezone?: string;
  onboardingState?: OnboardingState;
  createdAt?: string;
}

export interface ConfigVersion {
  id?: string;
  version: number;
  config: BrandConfig;
  changedBy?: string | null;
  note?: string | null;
  createdAt?: string;
}

// ─── Creatives ─────────────────────────────────────────────────────────────

export interface QaIssue {
  kind: string;
  detail: string;
  element?: string;
  severity: "minor" | "major";
}
export interface QaReport {
  verdict: "pass" | "fail";
  transcribedText: string[];
  expectedText: string[];
  issues: QaIssue[];
  aiLookScore?: number;
  model?: string;
  checkedAt?: string;
}

export interface CreativeAsset {
  id: string;
  creativeId?: string;
  aspect: string;
  mediaType: "image" | "video";
  url?: string | null;
  thumbnailUrl?: string | null;
  width?: number | null;
  height?: number | null;
  durationSec?: number | null;
  model?: string | null;
  status?: string;
  qaStatus?: "pending" | "pass" | "fail" | "skipped";
  qaReport?: QaReport | null;
  attempts?: number;
  error?: string | null;
}

export interface Creative {
  id: string;
  brandId?: string;
  batchId?: string | null;
  parentCreativeId?: string | null;
  generation?: number;
  name: string;
  kind: "image" | "video";
  concept?: string;
  hook?: string;
  angle?: string;
  emotionalTrigger?: EmotionalTrigger | string;
  awarenessStage?: AwarenessStage | string;
  format?: string;
  sourceReferenceId?: string | null;
  hookSource?: string | null;
  headline?: string | null;
  primaryText?: string | null;
  cta?: string | null;
  destinationKey?: string | null;
  overlayText?: string[];
  status: CreativeStatus | string;
  statusReason?: string | null;
  imported?: number | boolean;
  source?: "generated" | "uploaded" | "imported" | string;
  tags?: string[];
  perf?: Record<string, number | string | null> | null;
  assets?: CreativeAsset[];
  thumbnailUrl?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface Approval {
  id: string;
  creativeId: string;
  decision: "pending" | "approved" | "rejected";
  decidedBy?: string | null;
  decidedAt?: string | null;
  reasons?: RejectionReason[];
  note?: string | null;
  requestedAt?: string;
}

export interface Feedback {
  meta?: { autoFix?: boolean; rememberForBrand?: boolean; memoryProcessed?: boolean; brandRuleId?: string | null; correctionCreativeIds?: string[]; feedbackJobId?: string; feedbackJobStatus?: string | null };
  id: string;
  kind: string;
  reasons?: RejectionReason[];
  note?: string | null;
  rating?: number | null;
  userId?: string | null;
  createdAt?: string;
}

// ─── Recommendations / decisions ────────────────────────────────────────────

export type RecCategory = "risk" | "opportunity" | "decision_point";

export interface RecStep {
  tool: string;
  input: Record<string, unknown>;
}

/** recommendations.list item (learning `presentRec`): evidence is the one-line string; `details` the raw evidence. */
export interface Recommendation {
  id: string;
  brandId?: string;
  kind: string;
  /** "Risk" | "Opportunity" | "Decision Point" from the server (any casing accepted). */
  category?: RecCategory | string;
  severity?: "info" | "warning" | "critical";
  title: string;
  body?: string;
  evidence?: string | (Record<string, unknown> & { summary?: string; text?: string });
  details?: Record<string, unknown>;
  action?: { tool: string; input: Record<string, unknown>; label: string; steps?: RecStep[] } | null;
  targetType?: string | null;
  targetId?: string | null;
  target?: { type?: string | null; id?: string | null };
  status: "open" | "accepted" | "dismissed" | "done" | "expired" | "superseded" | string;
  createdAt?: string;
  /** YYYY-MM-DD */
  date?: string;
  /** "4d ago" (server-computed) */
  age?: string;
  decidedAt?: string | null;
}

export interface PendingAction {
  id: string;
  tool: string;
  brandId?: string | null;
  summary: string;
  input?: unknown;
  executable?: boolean;
  actorId?: string;
  actorType?: "agent" | "cron" | "system" | "user";
  actorName?: string;
  status?: string;
  createdAt?: string;
  expiresAt?: string;
}

export interface AdRow {
  id: string;
  externalId?: string;
  name: string;
  platform?: Platform | string;
  status?: string;
  effectiveStatus?: string | null;
  creativeId?: string | null;
  campaignName?: string;
  adSetName?: string;
  launchedAt?: string | null;
  thumbnailUrl?: string | null;
}

export interface ReferenceItem {
  id: string;
  brandId?: string | null;
  competitorId?: string | null;
  source: string;
  externalId?: string;
  url?: string | null;
  creator?: string | null;
  creatorFollowers?: number | null;
  pageName?: string | null;
  mediaType?: "image" | "video" | "carousel" | "text" | null;
  mediaUrl?: string | null;
  thumbnailUrl?: string | null;
  caption?: string | null;
  headline?: string | null;
  cta?: string | null;
  landingUrl?: string | null;
  metrics?: { views?: number; likes?: number; comments?: number; shares?: number; plays?: number };
  startDate?: string | null;
  endDate?: string | null;
  isActive?: boolean | null;
  daysRunning?: number | null;
  variantCount?: number | null;
  platforms?: string[];
  transcript?: string | null;
  frames?: { t: number; description: string; onScreenText?: string }[];
  formatDevices?: { hookTimingSec?: number; hookType?: string; ctaMechanism?: string; structure?: string; devices: string[] };
  hook?: string | null;
  angle?: string | null;
  emotionalTrigger?: string | null;
  awarenessStage?: string | null;
  tags?: string[];
  status?: "ok" | "needs_manual_upload" | "failed" | "pending_analysis" | string;
  statusDetail?: string | null;
  saved?: boolean;
  createdAt?: string;
}

export interface Alert {
  id: string;
  brandId?: string | null;
  kind: string;
  severity: "info" | "warning" | "critical";
  title: string;
  body?: string;
  status: "open" | "acknowledged" | "resolved";
  createdAt?: string;
  data?: Record<string, unknown>;
}

export interface DecisionsToday {
  approvals?: (Creative & { approvalId?: string })[];
  pendingActions?: PendingAction[];
  recommendations?: Recommendation[];
  manualUploads?: ReferenceItem[];
  publishedNotActive?: AdRow[];
  counts?: Record<string, number>;
  total?: number;
}

// ─── Normalisers ───────────────────────────────────────────────────────────

/** Return `v` if it's an array, else the first array found under `keys` (then "items"/"rows"/"data"). */
export function pickArray<T = unknown>(v: unknown, ...keys: string[]): T[] {
  if (Array.isArray(v)) return v as T[];
  if (v && typeof v === "object") {
    const o = v as Record<string, unknown>;
    for (const k of [...keys, "items", "rows", "data", "results"]) {
      if (Array.isArray(o[k])) return o[k] as T[];
    }
  }
  return [];
}

/** Return `v[key]` if it is an object, else `v` itself when it is an object. */
export function pickObject<T = Record<string, unknown>>(v: unknown, ...keys: string[]): T | null {
  if (!v || typeof v !== "object" || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  for (const k of keys) {
    const x = o[k];
    if (x && typeof x === "object" && !Array.isArray(x)) return x as T;
  }
  return o as T;
}

export function num(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v))) return Number(v);
  return null;
}

const RISK_KINDS = new Set(["kill", "graveyard", "retire", "fix_tracking", "geo", "budget", "pause"]);
const OPPORTUNITY_KINDS = new Set(["scale", "iterate", "revive", "rebuild_bench", "new_concept", "creative_refresh", "test_idea", "isolated_test"]);

/** Risk / Opportunity / Decision Point (PlayAd categories) from an explicit field or the rec kind. */
export function recCategory(r: Pick<Recommendation, "category" | "kind" | "severity">): RecCategory {
  const c = String(r.category ?? "").toLowerCase().replace(/[\s-]+/g, "_");
  if (c === "risk" || c === "opportunity" || c === "decision_point") return c;
  if (RISK_KINDS.has(r.kind)) return "risk";
  if (OPPORTUNITY_KINDS.has(r.kind)) return "opportunity";
  if (r.severity === "critical") return "risk";
  return "decision_point";
}

/** One-line evidence text for a recommendation card. */
export function recEvidence(r: Recommendation): string {
  if (typeof r.evidence === "string" && r.evidence) return r.evidence;
  const e = (r.evidence && typeof r.evidence === "object" ? r.evidence : (r.details ?? {})) as Record<string, unknown> & { summary?: string; text?: string };
  if (typeof e.summary === "string" && e.summary) return e.summary;
  if (typeof e.text === "string" && e.text) return e.text;
  if (r.body) return r.body;
  const parts = Object.entries(e)
    .filter(([, v]) => typeof v === "number" || typeof v === "string")
    .slice(0, 4)
    .map(([k, v]) => `${k.replace(/_/g, " ")} ${typeof v === "number" ? +v.toFixed(2) : v}`);
  return parts.join(" · ");
}

/** The best preview URL for a creative (prefer 1:1 image, then any image, then video poster). */
export function creativeThumb(c: Pick<Creative, "assets" | "thumbnailUrl">): string | null {
  if (c.thumbnailUrl) return c.thumbnailUrl;
  const assets = c.assets ?? [];
  const sq = assets.find((a) => a.aspect === "1:1" && a.url && a.mediaType === "image");
  const img = assets.find((a) => a.url && a.mediaType === "image");
  const vid = assets.find((a) => a.thumbnailUrl || a.url);
  return sq?.url ?? img?.url ?? vid?.thumbnailUrl ?? vid?.url ?? null;
}

export function creativeSource(c: Creative): "generated" | "uploaded" | "imported" {
  if (c.source === "uploaded" || c.source === "imported" || c.source === "generated") return c.source;
  if (c.imported === 1 || c.imported === true) return "imported";
  if (c.hookSource === "human" && !c.batchId) return "uploaded";
  return "generated";
}
